import React, { useContext, useState } from 'react';

import {
  ExternalEditInfoBanner,
  ImageUpload,
  useDialogContext,
  usePageContext,
} from '@xingternal/pages-common';
import {
  trackEditSaving,
  trackUpsellBannerForward,
} from '@xingternal/pages-tracking';
import { ErrorContext } from '@xingternal/pages-page-error';

import { UpdateCrop } from '../Images/Common';
import { UpsellBannerContainerProps } from './types';
import CompanyHeaderBanner from '../UpsellBanner/HeaderBannerImage';

type UpsellBannerUploadImageProps = {
  isEditCrop?: boolean;
  setNewCover: () => void;
  addMobileGuideline: () => void;
  discard: () => void;
  newOriginalCover?: string | null;
  handleClose: () => void;
};
export const UpsellBannerUploadImage: React.FC<UpsellBannerUploadImageProps> = ({
  isEditCrop,
  setNewCover,
  addMobileGuideline,
  discard,
  newOriginalCover,
  handleClose,
}) => {
  const { showError } = useContext(ErrorContext);
  const { pageContext } = usePageContext();
  const { nextStep, setImageCache } = useContext(UpsellBannerContainerContext);
  const { setDataChanged, dataChanged } = useDialogContext();

  return (
    <>
      {isEditCrop ? (
        <UpdateCrop
          isCover={true}
          descriptionCopyKey={'EP_COVER_CROP_DESCRIPTION'}
          setDataChanged={setDataChanged}
          setNewCover={setNewCover}
          onDragCrop={() => setDataChanged(true)}
          onCropReady={() => addMobileGuideline()}
          onDiscard={() => discard()}
          showError={showError}
          newOriginal={newOriginalCover}
          pageSlug={pageContext.pageSlug}
          closeOmview={handleClose}
          imageType={'cover'}
          aspectRatio={8 / 2}
          onPublish={() => {
            trackUpsellBannerForward({
              focusType: pageContext.focusType,
              itemId: pageContext.pageId,
            });
            nextStep();
          }}
          imageUploadCopy={{
            hint: 'EP_UPLOAD_IMAGE_HINT',
            label: 'EP_UPLOAD_COVER_LABEL',
            onDragLabel: 'EP_UPLOAD_IMAGE_DRAG_LABEL',
            discardButton: 'EP_UPSELL_BANNER_CONTAINER_DISCARD_BUTTON',
            publishButton: 'EP_UPSELL_BANNER_CONTAINER_PUBLISH_BUTTON',
          }}
        />
      ) : (
        <ImageUpload
          descriptionCopyKey={'EP_COVER_CROP_DESCRIPTION'}
          onCropReady={() => addMobileGuideline()}
          onDiscard={dataChanged ? () => discard() : () => handleClose()}
          onChange={() => setDataChanged(true)}
          showError={showError}
          shouldUploadOriginal={true}
          onPublish={(
            coverUploadId,
            cover,
            originalCoverUploadId,
            originalCover
          ) => {
            trackUpsellBannerForward({
              focusType: pageContext.focusType,
              itemId: pageContext.pageId,
            });
            setImageCache({
              coverUploadId,
              cover,
              originalCoverUploadId,
              originalCover,
            });
            nextStep();
          }}
          aspectRatio={8 / 2}
          imageUploadCopy={{
            headline: 'EP_CHOOSE_COVER',
            hint: 'EP_UPLOAD_IMAGE_HINT',
            label: 'EP_UPLOAD_COVER_LABEL',
            onDragLabel: 'EP_UPLOAD_IMAGE_DRAG_LABEL',
            discardButton: 'EP_UPSELL_BANNER_CONTAINER_DISCARD_BUTTON',
            publishButton: 'EP_UPSELL_BANNER_CONTAINER_PUBLISH_BUTTON',
          }}
        />
      )}
      <ExternalEditInfoBanner
        copyKey="EP_CHOOSE_COVER_INFO_BANNER"
        ctaUrl={`https://help.onlyfy.com/hc/de/articles/8803872010001-Erstbef%C3%BCllung-des-Employer-Branding-Profils-auf-XING#titel--/-headerbild`}
        ctaCopyKey="EP_CHOOSE_COVER_INFO_BANNER_CTA"
      />
    </>
  );
};

type UpsellBannerCompanyHeaderBannerProps = {
  onUpload: (
    coverUploadId: any,
    originalCoverUploadId: any,
    handleClose: () => void,
    cover: string,
    originalCover: string
  ) => void;
  handleClose: () => void;
};
export const UpsellBannerCompanyHeaderBanner: React.FC<UpsellBannerCompanyHeaderBannerProps> = ({
  onUpload,
  handleClose,
}) => {
  const { pageContext } = usePageContext();
  const { imageCache } = useContext(UpsellBannerContainerContext);

  return (
    <CompanyHeaderBanner
      onClick={() => {
        if (imageCache) {
          trackEditSaving({
            isFreePage: true,
            focusType: pageContext.focusType,
            itemId: pageContext.pageId,
            module: 'header',
            part: 'image',
          });

          const {
            coverUploadId,
            cover,
            originalCoverUploadId,
            originalCover,
          } = imageCache;

          onUpload(
            coverUploadId,
            originalCoverUploadId,
            handleClose,
            cover,
            originalCover
          );
        }
      }}
      backLink={`${pageContext?.basePath}/${pageContext?.pageSlug}`}
    />
  );
};

type ImageCacheType = {
  coverUploadId: string;
  cover: any;
  originalCoverUploadId: string;
  originalCover: any;
};
type UpsellBannerContainerContextType = {
  nextStep: () => void;
  imageCache: ImageCacheType | null;
  setImageCache: (imageCache: ImageCacheType) => void;
};
const UpsellBannerContainerContextDefaultValues = {
  nextStep: () => {},
  imageCache: null,
  setImageCache: () => {},
};
export const UpsellBannerContainerContext = React.createContext<UpsellBannerContainerContextType>(
  UpsellBannerContainerContextDefaultValues
);

const UpsellBannerContainer: React.FC<UpsellBannerContainerProps> = ({
  children,
  initialStep,
}) => {
  const [step, setStep] = useState<number>(initialStep);
  const [imageCache, setImageCache] = useState<ImageCacheType | null>(null);

  const nextStep = () => setStep(step + 1);

  return (
    <UpsellBannerContainerContext.Provider
      value={{ nextStep, imageCache, setImageCache }}
    >
      {React.Children.map(children, (child, index) =>
        index === step ? child : null
      )}
    </UpsellBannerContainerContext.Provider>
  );
};

export default UpsellBannerContainer;
